import { useState, useCallback, useEffect } from "react";
import { TimeSlot } from "@/types/booking";

/**
 * Independent Slot Selection State Management
 *
 * Each slot (first and second) has its own independent expansion state.
 * This allows editing either slot without affecting the other.
 */

interface UseSlotSelectionStateMachineProps {
  firstSlot: TimeSlot | undefined;
  secondSlot: TimeSlot | undefined;
}

export const useSlotSelectionStateMachine = ({
  firstSlot,
  secondSlot,
}: UseSlotSelectionStateMachineProps) => {
  // Independent expansion states for each slot
  const [isFirstSlotExpanded, setIsFirstSlotExpanded] = useState(!firstSlot);
  const [isSecondSlotExpanded, setIsSecondSlotExpanded] = useState(
    !!firstSlot && !secondSlot,
  );

  // Auto-expand first slot when it becomes empty (e.g., after deletion)
  useEffect(() => {
    if (!firstSlot && !isFirstSlotExpanded) {
      console.log("[State Machine] First slot deleted, re-expanding");
      setIsFirstSlotExpanded(true);
    }
  }, [firstSlot, isFirstSlotExpanded]);

  // Second slot should always be visible (even when first slot isn't selected)
  const showSecondSlot = true;

  // Toggle first slot expansion
  const onEditFirstSlot = useCallback(() => {
    console.log("[State Machine] Expanding first slot");
    setIsFirstSlotExpanded(true);
  }, []);

  const onDoneEditingFirstSlot = useCallback(() => {
    console.log("[State Machine] Collapsing first slot");
    setIsFirstSlotExpanded(false);
  }, []);

  // Toggle second slot expansion
  const onEditSecondSlot = useCallback(() => {
    console.log("[State Machine] Expanding second slot");
    setIsSecondSlotExpanded(true);
  }, []);

  const onAddSecondSlot = useCallback(() => {
    // Guard: don't open second slot if first slot doesn't exist
    // (e.g., it was invalidated by a clinic switch during the animation delay)
    if (!firstSlot) {
      console.log("[State Machine] Ignoring add second slot — no first slot");
      return;
    }
    console.log("[State Machine] Adding second slot (expanding)");
    setIsSecondSlotExpanded(true);
  }, [firstSlot]);

  const onDoneEditingSecondSlot = useCallback(() => {
    console.log("[State Machine] Collapsing second slot");
    setIsSecondSlotExpanded(false);
  }, []);

  const onDeleteSecondSlot = useCallback(() => {
    console.log("[State Machine] Deleting second slot (collapsing)");
    setIsSecondSlotExpanded(false);
  }, []);

  console.log("[State Machine] Current state:", {
    isFirstSlotExpanded,
    isSecondSlotExpanded,
    showSecondSlot,
    hasFirstSlot: !!firstSlot,
    hasSecondSlot: !!secondSlot,
  });

  return {
    // State flags
    isFirstSlotExpanded,
    isSecondSlotExpanded,
    showSecondSlot,
    // Transitions
    onEditFirstSlot,
    onDoneEditingFirstSlot,
    onEditSecondSlot,
    onAddSecondSlot,
    onDoneEditingSecondSlot,
    onDeleteSecondSlot,
  };
};
